/**
 * MiDash Analytics - Event Tracking System
 * Tracks user behavior for conversion optimization
 * Integrates with GA4 custom dimensions for attribution
 */

import { getSessionId, getDeviceId, getUTM } from '@/shared/utils/sessionTracker';
import { getRouteContext, getEventsEndpoint, isImmersiveInteraction, AnalyticsContext } from '@/shared/utils/routeContext';

// GA4 Measurement ID
const GA_MEASUREMENT_ID = 'G-YQP2FVGL35';

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
  }
}

interface EventData {
  [key: string]: string | number | boolean | null | undefined;
}

interface AnalyticsEvent {
  event_name: string;
  session_id: string;
  device_id: string;
  user_id?: string;
  page: string;
  timestamp: string;
  data: EventData;
}

// Context-keyed event queues for batching
let eventQueues: Record<AnalyticsContext, AnalyticsEvent[]> = {
  marketing: [],
  immersive: [],
  auth: [],
  platform: [],
};
let flushTimeout: ReturnType<typeof setTimeout> | null = null;

const BATCH_SIZE = 5;
const FLUSH_INTERVAL_MS = 10000; // 10 seconds

/**
 * Track a generic event
 */
export function trackEvent(eventName: string, data: EventData = {}): void {
  if (typeof window === 'undefined') return;

  const page = window.location.pathname;

  // Determine context — override to immersive for chat-like events on marketing pages
  let context = getRouteContext(page);
  if (context === 'marketing' && isImmersiveInteraction(eventName, page)) {
    context = 'immersive';
  }

  const platform = window.innerWidth < 768 ? 'mobile' : 'desktop';

  const event: AnalyticsEvent = {
    event_name: eventName,
    session_id: getSessionId(),
    device_id: getDeviceId(),
    page,
    timestamp: new Date().toISOString(),
    data: { ...data, platform },
  };

  // Attach user_id for platform context (authenticated users)
  if (context === 'platform') {
    const uid = localStorage.getItem('userId') || localStorage.getItem('user_id');
    if (uid) event.user_id = uid;
  }

  eventQueues[context].push(event);

  console.log(`[Analytics] Event: ${eventName} [${context}]`, data);

  // Flush if total across all queues reaches batch size
  const totalQueued = Object.values(eventQueues).reduce((sum, q) => sum + q.length, 0);
  if (totalQueued >= BATCH_SIZE) {
    flushEvents();
  } else if (!flushTimeout) {
    flushTimeout = setTimeout(flushEvents, FLUSH_INTERVAL_MS);
  }

  // Push to GTM dataLayer — single source of truth for all third-party tags
  // (GA4, Amplitude, Meta Pixel, etc. are all configured as GTM tags)
  if (window.dataLayer) {
    window.dataLayer.push({
      event: eventName,
      ...data,
      session_id: getSessionId(),
      device_id: getDeviceId(),
      utm_source: getUTM('source'),
      utm_medium: getUTM('medium'),
      utm_campaign: getUTM('campaign'),
    });
  }
}

/**
 * Track event immediately via sendBeacon (for beforeunload — bypasses batching race condition)
 */
export function trackEventBeacon(eventName: string, data: EventData = {}): void {
  if (typeof window === 'undefined' || !navigator.sendBeacon) return;

  const page = window.location.pathname;
  let context = getRouteContext(page);
  if (context === 'marketing' && isImmersiveInteraction(eventName, page)) {
    context = 'immersive';
  }

  const platform = window.innerWidth < 768 ? 'mobile' : 'desktop';
  const event: AnalyticsEvent = {
    event_name: eventName,
    session_id: getSessionId(),
    device_id: getDeviceId(),
    page,
    timestamp: new Date().toISOString(),
    data: { ...data, platform },
  };

  navigator.sendBeacon(
    getEventsEndpoint(context),
    JSON.stringify({ events: [event] })
  );
}

/**
 * Track page view
 */
export function trackPageView(page: string, metadata: EventData = {}): void {
  trackEvent('page_view', {
    page,
    referrer: document.referrer,
    title: document.title,
    ...metadata,
  });
}

/**
 * Track scroll depth
 */
let maxScrollDepth = 0;
let scrollDepthTracked = { 25: false, 50: false, 75: false, 100: false };
let scrollStartTime = Date.now();

export function initScrollTracking(): void {
  if (typeof window === 'undefined') return;

  scrollStartTime = Date.now();
  maxScrollDepth = 0;
  scrollDepthTracked = { 25: false, 50: false, 75: false, 100: false };

  const handleScroll = () => {
    const scrollTop = window.scrollY;
    const docHeight = document.documentElement.scrollHeight - window.innerHeight;

    if (docHeight <= 0) return;

    const scrollPercent = Math.round((scrollTop / docHeight) * 100);

    if (scrollPercent > maxScrollDepth) {
      maxScrollDepth = scrollPercent;
    }

    // Track milestone depths
    const milestones = [25, 50, 75, 100] as const;
    for (const depth of milestones) {
      if (scrollPercent >= depth && !scrollDepthTracked[depth]) {
        scrollDepthTracked[depth] = true;
        trackEvent('scroll_depth', {
          depth,
          time_to_depth_ms: Date.now() - scrollStartTime,
        });
      }
    }
  };

  // Throttle scroll handler
  let scrollTimeout: ReturnType<typeof setTimeout> | null = null;
  window.addEventListener('scroll', () => {
    if (!scrollTimeout) {
      scrollTimeout = setTimeout(() => {
        handleScroll();
        scrollTimeout = null;
      }, 100);
    }
  });
}

/**
 * Track CTA button click
 */
export function trackClick(buttonText: string, section: string, metadata: EventData = {}): void {
  trackEvent('cta_click', {
    button_text: buttonText,
    section,
    ...metadata,
  });
}

/**
 * Track section entering viewport
 */
export function trackSectionView(sectionName: string, timeVisible: number): void {
  trackEvent('section_view', {
    section_name: sectionName,
    time_visible_ms: timeVisible,
  });
}

/**
 * Track form interactions
 */
let formStarted: { [formName: string]: boolean } = {};
let formFields: { [formName: string]: string[] } = {};

export function trackFormStart(formName: string): void {
  if (!formStarted[formName]) {
    formStarted[formName] = true;
    formFields[formName] = [];
    trackEvent('form_start', { form_name: formName });
  }
}

export function trackFormField(formName: string, fieldName: string, hasValue: boolean): void {
  if (!formFields[formName]) {
    formFields[formName] = [];
  }
  if (!formFields[formName].includes(fieldName)) {
    formFields[formName].push(fieldName);
  }
  trackEvent('form_field', {
    form_name: formName,
    field_name: fieldName,
    has_value: hasValue,
  });
}

export function trackFormSubmit(formName: string, success: boolean, error?: string): void {
  trackEvent('form_submit', {
    form_name: formName,
    success,
    error: error || null,
    fields_filled: formFields[formName]?.length || 0,
  });
  // Reset form tracking
  delete formStarted[formName];
  delete formFields[formName];
}

export function trackFormAbandon(formName: string): void {
  if (formStarted[formName] && formFields[formName]?.length > 0) {
    trackEvent('form_abandon', {
      form_name: formName,
      fields_filled: formFields[formName].length,
      last_field: formFields[formName][formFields[formName].length - 1],
    });
  }
}

/**
 * Track demo/chat interactions
 */
export function trackDemoQuery(queryPreview: string): void {
  trackEvent('demo_query', {
    query_preview: queryPreview.substring(0, 50),
    query_length: queryPreview.length,
  });
}

export function trackDemoLimitHit(): void {
  trackEvent('demo_limit_hit', {});
}

/**
 * Track waitlist modal
 */
export function trackWaitlistModalOpen(trigger: 'limit' | 'cta' | 'auto'): void {
  trackEvent('waitlist_modal_open', { trigger });
}

export function trackWaitlistSubmit(userType: string, hasChallenge: boolean): void {
  trackEvent('waitlist_submit', {
    user_type: userType,
    has_challenge: hasChallenge,
  });

  // Update GA4 user properties to 'waitlist' (conversion)
  setGA4UserProperties('waitlist');

  // Send dedicated GA4 conversion event
  if (isGA4Available()) {
    window.gtag!('event', 'waitlist_signup', {
      user_type: userType,
      session_id: getSessionId(),
      device_id: getDeviceId(),
      utm_source: getUTM('source'),
      utm_campaign: getUTM('campaign'),
    });
  }
}

/**
 * Track conversion (generic)
 */
export function trackConversion(type: string, data: EventData = {}): void {
  trackEvent('conversion', {
    conversion_type: type,
    ...data,
  });
}

/**
 * Flush events to backend
 */
async function flushEvents(): Promise<void> {
  if (flushTimeout) {
    clearTimeout(flushTimeout);
    flushTimeout = null;
  }

  const contexts: AnalyticsContext[] = ['marketing', 'immersive', 'auth', 'platform'];

  for (const context of contexts) {
    const queue = eventQueues[context];
    if (queue.length === 0) continue;

    const eventsToSend = [...queue];
    eventQueues[context] = [];

    try {
      const response = await fetch(getEventsEndpoint(context), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ events: eventsToSend }),
      });

      if (!response.ok) {
        // Put events back in the correct context queue on failure
        eventQueues[context] = [...eventsToSend, ...eventQueues[context]];
        console.warn(`[Analytics] Failed to send ${context} events:`, response.status);
      } else {
        console.log(`[Analytics] Flushed ${eventsToSend.length} ${context} events`);
      }
    } catch (error) {
      // Put events back in the correct context queue on error
      eventQueues[context] = [...eventsToSend, ...eventQueues[context]];
      console.warn(`[Analytics] Error sending ${context} events:`, error);
    }
  }
}

/**
 * Check if GA4 is available
 */
function isGA4Available(): boolean {
  return typeof window !== 'undefined' && typeof window.gtag === 'function';
}

/**
 * Send event to GA4 with custom dimensions
 */
function sendToGA4(eventName: string, data: EventData): void {
  if (!isGA4Available()) return;

  window.gtag!('event', eventName, {
    ...data,
    session_id: getSessionId(),
    device_id: getDeviceId(),
    utm_source: getUTM('source'),
    utm_campaign: getUTM('campaign'),
  });
}

/**
 * Set GA4 user properties (custom dimensions at user scope)
 */
export function setGA4UserProperties(userType: 'visitor' | 'waitlist' | 'beta_user'): void {
  if (!isGA4Available()) return;

  window.gtag!('set', 'user_properties', {
    device_id: getDeviceId(),
    user_type: userType,
  });
}

/**
 * Send enriched page view to GA4
 */
export function sendGA4PageView(pagePath?: string): void {
  if (!isGA4Available()) return;

  window.gtag!('event', 'page_view', {
    page_path: pagePath || window.location.pathname,
    page_title: document.title,
    session_id: getSessionId(),
    device_id: getDeviceId(),
    utm_source: getUTM('source'),
    utm_medium: getUTM('medium'),
    utm_campaign: getUTM('campaign'),
  });
}

/**
 * Initialize GA4 with custom dimensions - call on app load
 */
export function initGA4(): void {
  if (!isGA4Available()) {
    console.warn('[GA4] gtag not available');
    return;
  }

  // Set initial user properties
  setGA4UserProperties('visitor');

  // Send initial page view with custom dimensions
  sendGA4PageView();

  console.log('[GA4] Initialized with custom dimensions');
}

/**
 * Flush events before page unload
 */
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    if (!navigator.sendBeacon) return;

    const contexts: AnalyticsContext[] = ['marketing', 'immersive', 'auth', 'platform'];
    for (const context of contexts) {
      if (eventQueues[context].length > 0) {
        navigator.sendBeacon(
          getEventsEndpoint(context),
          JSON.stringify({ events: eventQueues[context] })
        );
      }
    }
  });
}
